import { Fork, Invoke, nextPointer } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFCitizenship from '../components/RMFCitizenship/RMFCitizenship';
import RMFCrossOperatorDepositLimit from '../components/RMFCrossOperatorDepositLimit/RMFCrossOperatorDepositLimit';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPhone from '../components/RMFPhone/RMFPhone';
import RMFPhoneVerification from '../components/RMFPhoneVerification/RMFPhoneVerification';
import RMFPlaceOfBirth from '../components/RMFPlaceOfBirth/RMFPlaceOfBirth';
import RMFResult from '../components/RMFResult/RMFResult';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFSummaryScreen from '../components/RMFSummaryScreen/RMFSummaryScreen';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfDEFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();
  const invokePhoneVerificationSave = new Invoke(
    Service.RmfMobilePhoneVerificationSave,
    RMFLegalName,
    RMFPhoneVerification
  );

  const forkPhoneVerificationCheck = new Fork(
    Guard.PhoneVerificationCheck,
    [RMFPhoneVerification, invokePhoneVerificationSave, RMFLegalName],
    [RMFLegalName]
  );

  const invokeRMFPhoneSave = new Invoke(
    Service.RmfMobilePhoneSave,
    forkPhoneVerificationCheck,
    RMFPhone
  );

  const forkMobilePhoneVerifiedCheck = new Fork(
    Guard.CheckMobilePhoneVerified,
    [RMFLegalName],
    [RMFPhone, invokeRMFPhoneSave, forkPhoneVerificationCheck]
  );

  const invokeRmfDobUpdate = new Invoke(Service.RmfDobUpdate, forkMobilePhoneVerifiedCheck, RMFDOB);

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate],
    [forkMobilePhoneVerifiedCheck]
  );
  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [forkSignupDepositCheckDone]
  );

  const retryMarker = {
    name: 'retryMarker',
    after: { 0: nextPointer }
  };

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const invokeResultEnableRealMoneyFlag = new Invoke(
    Service.RmfEnableRealMoneyFlagDE,
    forkRmfExit,
    RMFResult
  );

  const invokeDepositLimitResultSave = new Invoke(
    Service.RmfCrossOperatorDepositLimitSave,
    invokeResultEnableRealMoneyFlag,
    RMFResult
  );

  const forkRetryExit = new Fork(
    Guard.CheckIsDEUserRMOK,
    [RMFVerification, forkRmfExit],
    [RMFResult, invokeDepositLimitResultSave]
  );

  const invokeRetryEnableRealMoneyFlag = new Invoke(Service.RmfEnableRealMoneyFlagDE, retryMarker);

  const invokeRetryDEDataSave = new Invoke(
    Service.RmfRetryDEUserDataSave,
    invokeRetryEnableRealMoneyFlag,
    RMFSummaryScreen
  );

  const forkEKYCElectronicCheckFailed = new Fork(
    Guard.CheckEKYCRetry,
    [RMFSummaryScreen, invokeRetryDEDataSave],
    [RMFSummaryScreen]
  );

  const forkEKYCFailCheck = new Fork(
    Guard.CheckEKYCFailed,
    [forkEKYCElectronicCheckFailed],
    [forkRetryExit]
  );

  const invokeEnableRealMoneyFlag = new Invoke(Service.RmfEnableRealMoneyFlagDE, forkEKYCFailCheck);

  const invokeDepositLimitSave = new Invoke(
    Service.RmfCrossOperatorDepositLimitSave,
    invokeEnableRealMoneyFlag,
    RMFCrossOperatorDepositLimit
  );

  const invokeBirthNameSave = new Invoke(Service.RmfBirthNameSave, RMFAddress, RMFLegalName);
  const invokeLegalNameSave = new Invoke(
    Service.RmfLegalNameSave,
    invokeBirthNameSave,
    RMFLegalName
  );

  const invokeAddressSave = new Invoke(Service.RmfAddressDESave, RMFPlaceOfBirth, RMFAddress);
  const invokePlaceOfBirthSave = new Invoke(
    Service.RmfPlaceOfBirthSave,
    RMFCitizenship,
    RMFPlaceOfBirth
  );

  const invokeCitizenshipSave = new Invoke(
    Service.RmfCitizenshipSave,
    RMFCrossOperatorDepositLimit,
    RMFCitizenship
  );

  const forkDepositLimits = new Fork(Guard.LimitsSelected, invokeEnableRealMoneyFlag, [
    invokeDepositLimitSave,
    invokeEnableRealMoneyFlag
  ]);

  return [
    invokeSignupDob,
    forkDobCheck,
    forkMobilePhoneVerifiedCheck,
    invokeLegalNameSave,
    invokeBirthNameSave,
    RMFAddress,
    invokeAddressSave,
    RMFPlaceOfBirth,
    invokePlaceOfBirthSave,
    RMFCitizenship,
    invokeCitizenshipSave,
    RMFCrossOperatorDepositLimit,
    forkDepositLimits,
    retryMarker,
    forkEKYCFailCheck,
    RMFSummaryScreen,
    invokeRetryDEDataSave,
    invokeRetryEnableRealMoneyFlag,
    forkRetryExit,
    jumpToFeatureStep
  ];
};

export default useRmfDEFlow;
